import { ColorTypes, ComponentAdapter } from "@omegagrid/core";
import { MarkdownLinkEvent } from "@omegagrid/markdown";
import { TreeAdapter, TreeOptions, TreeSource } from "@omegagrid/tree";
import { HistoryItem, ToolbarItemOptions } from "../types";
import { MarkdownEditor } from "./markdownEditor";

const DEFAULT_MARKDOWN_EDITOR_OPTIONS: MarkdownEditorOptions = {
	toolbar: {
		refresh: {
			color: ColorTypes.Gray,
			icon: 'arrows-rotate',
			enabled: true
		},
		save: {
			text: 'Save changes',
			color: ColorTypes.Green,
			icon: 'save',
			enabled: true
		},
		discard: {
			text: 'Discard changes',
			color: ColorTypes.Red,
			icon: 'floppy-disk-circle-xmark',
			enabled: true
		},
		spellcheck: {
			title: 'Spell check',
			color: ColorTypes.Gray,
			icon: 'spell-check',
			enabled: true
		},
		source: {
			title: 'Markdown source',
			color: ColorTypes.Gray,
			icon: 'code',
			enabled: true
		}
	}
}

export type MarkdownEditorHistoryOptions = {
	enabled?: boolean,
	placeholder?: string
}

export type MarkdownEditorToolbarOptions = {
	save?: ToolbarItemOptions,
	discard?: ToolbarItemOptions,
	refresh?: ToolbarItemOptions,
	spellcheck?: ToolbarItemOptions,
	source?: ToolbarItemOptions,
}

export type MarkdownEditorOptions = {
	readOnly?: boolean,
	/** Initial state of the spell-check toggle in the toolbar. Default false. */
	spellcheck?: boolean,
	pageSize?: number,
	language?: string,
	history?: boolean | MarkdownEditorHistoryOptions,
	toolbar?: MarkdownEditorToolbarOptions,
	disableImgUpload?: boolean
}

export type MarkdownContentOptions = {
	offset?: number,
	limit?: number
};

export type MarkdownImage = {
	idz?: number;
	url?: string;
	file?: File;
};

export abstract class MarkdownEditorAdapter<TEditor extends MarkdownEditor> extends ComponentAdapter<TEditor> {
	public editor: MarkdownEditor;
	abstract getContent(opt?: MarkdownContentOptions) : string|Promise<string>;
	getHistoryContent(idz: number, opt?: MarkdownContentOptions) : string|Promise<string> {
		return null;
	}
	save(content: string) : void|Promise<void> {};
	getOptions(): null|Partial<MarkdownEditorOptions>|Promise<Partial<MarkdownEditorOptions>> { 
		return DEFAULT_MARKDOWN_EDITOR_OPTIONS;	
	}
	getHistoryItems() : Partial<HistoryItem[]>|Promise<Partial<HistoryItem[]>> { return []; }
	getCurrentItem() : HistoryItem|Promise<HistoryItem> {
		return null;
	}
	createOptions(options: Partial<MarkdownEditorOptions>): MarkdownEditorOptions {
		return Object.assign({}, DEFAULT_MARKDOWN_EDITOR_OPTIONS, options);
	}
	uploadFile(file: File): Promise<string> { return null; }
	modifyImageUrl(url: string): string { return url; }
	modifyLinkUrl(href: string): string { return href; }
	onLink(e: MarkdownLinkEvent): void|Promise<void> {
		const href = e.href ?? e.a.getAttribute('href');
		if (!href) return;
		window.open(href, '_blank');
	};
}

export class MarkdownHistoryAdapter extends TreeAdapter {

	private items: TreeSource = null;

	constructor(private adapter: MarkdownEditorAdapter<MarkdownEditor>) {
		super();
	}

	invalidateItems() {
		this.items = null;
	}

	formatItem(item: HistoryItem): string {
		if (!item.date_created) return '';
		return `${item.date_created.substring(0, 19)} - ${item.who_created_dv ?? item.who_created ?? '<unknown>'}`;
	}

	async getTree(): Promise<TreeSource> {
		if (this.items === null) {
			const items = await this.adapter.getHistoryItems();
			this.items = items.map(item => ({
				k: item.idz.toString(),
				v: this.formatItem(item),
			}));
		}
		return this.items;
	}

    getOptions(): Partial<TreeOptions> {
		return {
			selectMode: 'single',
			autoClose: true,
		};
	}

}
